import { Security } from '@blk/awt-aladdinportfoliobuilderserver';
import { CancelToken } from 'axios';
import { getLiveSecuritiesByCusip, postListSecuritiesByCusip } from '../api/AladdinPortfolioBuilderServer';

type MappedSecurities = { [s: string]: Security };

/**
 * This function will attempt to get securities from the normal aw-security endpoint. But if some are missing, it will
 * go to the 'live' endpoint as well to try to fill in the gaps. The 'live' endpoint returns the latest version of all
 * historical securities, even if they have been purged from the regular security universe.
 * More information here: https://webster.bfm.com/jira/browse/AWM-19505
 * @param cusips
 * @param date
 * @param pkg
 * @param cancelToken
 */
export async function fetchSecuritiesByCusipWithLiveFallback(
  cusips: string[],
  date: string,
  pkg: string,
  cancelToken?: CancelToken
) {
  const securities = await postListSecuritiesByCusip(cusips, date, pkg, cancelToken);
  if (securities.length === cusips.length) {
    return securities;
  }
  const mappedSecuritiesFromRegularCall = securities.reduce<MappedSecurities>((memo, next) => {
    memo[next.cusip] = next;
    return memo;
  }, {});

  const missingCusips = cusips.filter((cusip) => !mappedSecuritiesFromRegularCall[cusip]);

  const liveSecurities = await getLiveSecuritiesByCusip(missingCusips, date, cancelToken);

  const mappedSecuritiesFromLiveCall = liveSecurities.reduce<MappedSecurities>((memo, next) => {
    memo[next.cusip] = next;
    return memo;
  }, {});

  const results: Security[] = [];

  // Cusips should be ordered as requested as that is how the apis operate independently
  cusips.forEach((cusip) => {
    const security = mappedSecuritiesFromRegularCall[cusip] || mappedSecuritiesFromLiveCall[cusip];
    if (security) {
      results.push(security);
    }
  });
  return results;
}
